<template>
    <div class="charge">
        <div class="info">
            <div v-if="displayZeroAmount || hasAmount" class="amount">
                {{ amountFormatted }}
            </div>
            <PaymentMethodIcons :paymentInformations="paymentInformations" />
        </div>
        <div v-if="isStripePayment || isWalleeTastierSpace" class="hint">
            {{ trans.orderReceivedPopover.hint }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import type { IOrderPaymentConfirmation } from "@/data/model/Customer";
    import { PaymentProvider } from "@/data/model/Enums";
    import { config } from "@/data/util/Config";
    import { Format } from "@/data/util/Format";
    import { computed } from "vue";

    import PaymentMethodIcons from "@/app/components/payment/PaymentMethodIcons.vue";

    const props = defineProps<{
        confirmation: IOrderPaymentConfirmation;
        displayZeroAmount?: boolean;
    }>();

    // Payment
    const isWalleeTastierSpace = computed(() =>
        props.confirmation.paymentMethodSplitInformation.some(
            (pms) =>
                (pms.provider === PaymentProvider.Wallee || pms.provider === PaymentProvider.PostFinance) &&
                pms.merchantId === config.walleeTastierSpaceId
        )
    );
    const isStripePayment = computed(() =>
        props.confirmation.paymentMethodSplitInformation.some((pms) => pms.provider === PaymentProvider.Stripe)
    );
    const hasAmount = computed(() => props.confirmation.amount > 0);
    const amountFormatted = computed(() =>
        Format.toMoneyFormat(props.confirmation.amount, props.confirmation.currency)
    );
    const paymentInformations = computed(() => props.confirmation.paymentMethodSplitInformation ?? []);
</script>

<style lang="scss">
    .order-received-popover-content,
    .payment-received-popover-content {
        .charge {
            text-align: center;

            > .info {
                display: inline-flex;

                > .amount {
                    font-size: 14px;
                    line-height: 21px;
                }

                .icon {
                    min-width: 26px;
                    background-size: contain;
                    background-repeat: no-repeat;
                    background-position: center center;
                    margin: 2px 8px 2px 0px;
                    display: inline-block;
                    vertical-align: top;
                }

                .digits {
                    display: inline-block;
                    margin-right: 8px;
                    vertical-align: top;
                    font-size: 14px;
                    line-height: 21px;

                    > .bullets {
                        letter-spacing: 1px;
                        font-size: 10px;
                        line-height: 21px;
                        padding-right: 2px;
                        display: inline-block;
                        vertical-align: top;
                    }
                }
            }

            .hint {
                font-size: 12px;
            }
        }
    }
</style>
